// Entra sign-in (MSAL, auth code + PKCE). Every page needs a signed-in user: the app is
// "assignment required", so only people in the Ask HR groups get this far.

import {
  InteractionRequiredAuthError,
  PublicClientApplication,
  type AccountInfo,
} from "@azure/msal-browser";
import { rolesFromClaims, type RoleName } from "@ask-hr/core/roles";
import { apiScope, clientId, tenantId } from "../config.js";

export const msal = new PublicClientApplication({
  auth: {
    clientId,
    authority: `https://login.microsoftonline.com/${tenantId}`,
    redirectUri: `${window.location.origin}/`,
    postLogoutRedirectUri: `${window.location.origin}/`,
  },
  cache: { cacheLocation: "sessionStorage" },
});

// Finishes a sign-in redirect if this page load is one, then returns the signed-in account.
export async function currentAccount(): Promise<AccountInfo | null> {
  await msal.initialize();
  const result = await msal.handleRedirectPromise();
  const account = result?.account ?? msal.getActiveAccount() ?? msal.getAllAccounts()[0] ?? null;
  if (account) msal.setActiveAccount(account);
  return account;
}

export function signIn(): Promise<void> {
  return msal.loginRedirect({ scopes: [apiScope], redirectStartPage: window.location.href });
}

export function signOut(): Promise<void> {
  return msal.logoutRedirect();
}

export async function apiToken(): Promise<string> {
  const account = msal.getActiveAccount();
  if (!account) {
    await signIn();
    throw new Error("signing in");
  }
  try {
    return (await msal.acquireTokenSilent({ scopes: [apiScope], account })).accessToken;
  } catch (e) {
    if (e instanceof InteractionRequiredAuthError) await msal.acquireTokenRedirect({ scopes: [apiScope], account });
    throw e;
  }
}

// From the ID token, for showing or hiding pages. The API checks the access token's roles itself.
export function rolesOf(account: AccountInfo): RoleName[] {
  return rolesFromClaims(account.idTokenClaims ?? {});
}
